import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { DEFAULT_DEBOUNCE_DELAY } from "@/lib/constants";
import { ApiKey } from "@/types/api-keys";
import { Input } from "./ui/input";
import { useState } from "react";
import { useOrganizationStore } from "@/stores/organization";
import { LoaderCircle } from "lucide-react";

interface ApiKeysDeleteDialogProps {
  apiKey: ApiKey;
  trigger?: React.ReactNode;
}

export default function ApiKeysDeleteDialog({
  apiKey,
  trigger,
}: ApiKeysDeleteDialogProps) {
  const { deleteApiKey } = useOrganizationStore();
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteApiKey = async (
    apiKeyId: string,
    organizationId: string,
  ) => {
    try {
      setIsDeleting(true);
      await deleteApiKey(apiKeyId, organizationId);
      setOpen(false);
    } catch (error: any) {
      console.warn(error.message);
    }
  };

  const debouncedOnDelete = useDebounce(
    handleDeleteApiKey,
    DEFAULT_DEBOUNCE_DELAY,
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent className="bg-card">
        <DialogHeader className="flex">
          <DialogTitle className="text-gray-1100 dark:text-gray-100">
            Revoke secret key
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-2 text-secondary">
            This API key will immediately be disabled. Any requests using this
            API key will be rejected, which could cause any systems still
            depending on it. Once revoked, you&apos;ll no longer be able to view
            this key.
            <Input
              id="sk"
              className="text-gray-1100 dark:text-gray-100"
              placeholder="sk_...1234"
              value={apiKey.apiKey}
              type="text"
              disabled
            />
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2">
          <DialogClose asChild>
            <Button variant="form">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={() => debouncedOnDelete(apiKey.id, apiKey.organizationId)}
          >
            {isDeleting && (
              <LoaderCircle className="flex size-6 animate-spin text-destructive-foreground" />
            )}
            Revoke
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
